import { useCallback, useEffect, useRef, useState } from "react";
import { getESignature } from "@/features/esignatures/api";
import type { ESignatureRequestDetail } from "@/features/esignatures/types";
import { ApiError } from "@/lib/api";

interface UseESignatureResult {
  request: ESignatureRequestDetail | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetches a single e-signature request (recipients + audit trail). */
export function useESignature(id: string): UseESignatureResult {
  const [request, setRequest] = useState<ESignatureRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    getESignature(id)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setRequest(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setRequest(null);
        setError(err instanceof ApiError ? err.message : "Failed to load request.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [id, reloadKey]);

  return { request, loading, error, refetch };
}
